import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquare, Plus, Send, Sparkles, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { postSse } from "../lib/sse";
import type { ChatMessage, Citation } from "../lib/types";

interface Props {
  onClose: () => void;
  onOpenNote: (id: string) => void;
}

interface DraftMsg {
  id: string;
  role: "user" | "assistant";
  content: string;
  citations?: Citation[];
  streaming?: boolean;
}

export function ChatPanel({ onClose, onOpenNote }: Props) {
  const qc = useQueryClient();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [drafts, setDrafts] = useState<DraftMsg[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ctrlRef = useRef<AbortController | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const sessionsQ = useQuery({
    queryKey: ["chatSessions"],
    queryFn: () => api.listChatSessions(),
  });

  // Beim ersten Mount: erste Session auswählen oder neu anlegen
  const createSessionMut = useMutation({
    mutationFn: (title?: string) => api.createChatSession(title),
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ["chatSessions"] });
      setSessionId(s.id);
      setDrafts([]);
    },
  });

  const deleteSessionMut = useMutation({
    mutationFn: (id: string) => api.deleteChatSession(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chatSessions"] });
      setSessionId(null);
      setDrafts([]);
    },
  });

  useEffect(() => {
    if (!sessionId && sessionsQ.data) {
      if (sessionsQ.data.length > 0) setSessionId(sessionsQ.data[0].id);
    }
  }, [sessionsQ.data, sessionId]);

  const messagesQ = useQuery({
    queryKey: ["chatMessages", sessionId],
    queryFn: () => api.listChatMessages(sessionId!),
    enabled: !!sessionId && !streaming,
  });

  // Merge persisted messages + drafts in display
  const display: DraftMsg[] = [
    ...(messagesQ.data?.map<DraftMsg>((m: ChatMessage) => ({
      id: m.id,
      role: m.role === "system" ? "assistant" : m.role,
      content: m.content,
      citations: m.citations ?? undefined,
    })) ?? []),
    ...drafts,
  ];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [display.length, drafts]);

  function send() {
    const text = input.trim();
    if (!text || !sessionId || streaming) return;
    setError(null);
    setInput("");

    const userDraft: DraftMsg = {
      id: `u-${Date.now()}`,
      role: "user",
      content: text,
    };
    const asstDraft: DraftMsg = {
      id: `a-${Date.now()}`,
      role: "assistant",
      content: "",
      streaming: true,
    };
    setDrafts([userDraft, asstDraft]);
    setStreaming(true);

    ctrlRef.current = postSse(
      `/api/chat/sessions/${sessionId}/messages`,
      { content: text },
      {
        onToken: (delta) => {
          setDrafts((prev) =>
            prev.map((d) =>
              d.id === asstDraft.id ? { ...d, content: d.content + delta } : d,
            ),
          );
        },
        onDone: (final) => {
          const cits = (final.citations as Citation[] | undefined) ?? [];
          setDrafts((prev) =>
            prev.map((d) =>
              d.id === asstDraft.id ? { ...d, streaming: false, citations: cits } : d,
            ),
          );
          setStreaming(false);
          // Drafts in persisted messages umwandeln
          setTimeout(() => {
            setDrafts([]);
            qc.invalidateQueries({ queryKey: ["chatMessages", sessionId] });
          }, 100);
        },
        onError: (err) => {
          setError(err);
          setStreaming(false);
          setDrafts((prev) =>
            prev.map((d) =>
              d.id === asstDraft.id ? { ...d, streaming: false, content: d.content || "—" } : d,
            ),
          );
        },
      },
    );
  }

  function cancel() {
    ctrlRef.current?.abort();
    setStreaming(false);
  }

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex w-[26rem] flex-col border-l border-zinc-800 bg-zinc-950 shadow-2xl">
      <header className="flex items-center justify-between border-b border-zinc-800 p-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Sparkles className="h-4 w-4 text-emerald-400" />
          RAG-Chat
        </div>
        <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200">
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="flex items-center gap-1 border-b border-zinc-800 p-2">
        <select
          value={sessionId ?? ""}
          onChange={(e) => {
            setSessionId(e.target.value || null);
            setDrafts([]);
          }}
          className="flex-1 rounded border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs"
        >
          <option value="">— Session wählen —</option>
          {sessionsQ.data?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title} · {new Date(s.created_at).toLocaleDateString("de-DE")}
            </option>
          ))}
        </select>
        <button
          onClick={() => createSessionMut.mutate(undefined)}
          title="Neue Session"
          className="rounded border border-zinc-800 p-1.5 hover:border-emerald-500 hover:text-emerald-400"
        >
          <Plus className="h-3 w-3" />
        </button>
        {sessionId && (
          <button
            onClick={() => {
              if (window.confirm("Session löschen?")) deleteSessionMut.mutate(sessionId);
            }}
            title="Session löschen"
            className="rounded border border-zinc-800 p-1.5 hover:border-red-500 hover:text-red-400"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto p-3 text-sm">
        {!sessionId && (
          <div className="rounded border border-zinc-800 bg-zinc-900/40 p-4 text-center text-xs text-zinc-500">
            <MessageSquare className="mx-auto mb-2 h-5 w-5" />
            Wähle eine Session oder lege eine neue an.
          </div>
        )}
        {sessionId && display.length === 0 && (
          <div className="text-center text-xs italic text-zinc-600">
            Stell eine Frage zu deinen Notes…
          </div>
        )}
        {display.map((m) => (
          <Message key={m.id} m={m} onOpenNote={onOpenNote} />
        ))}
        {error && (
          <div className="rounded border border-red-900/50 bg-red-950/40 p-2 text-xs text-red-300">
            {error}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <footer className="border-t border-zinc-800 p-2">
        <div className="flex items-end gap-1">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            disabled={!sessionId || streaming}
            placeholder={
              sessionId ? "Frage stellen… (Enter senden, Shift+Enter Newline)" : "Erst Session wählen"
            }
            rows={2}
            className="flex-1 resize-none rounded border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs focus:border-emerald-500 focus:outline-none disabled:opacity-50"
          />
          {streaming ? (
            <button
              onClick={cancel}
              className="rounded border border-zinc-700 bg-zinc-900 p-2 text-xs text-zinc-300"
            >
              Stop
            </button>
          ) : (
            <button
              onClick={send}
              disabled={!input.trim() || !sessionId}
              className="rounded border border-zinc-700 bg-emerald-700/30 p-2 text-emerald-300 hover:border-emerald-500 disabled:opacity-40"
            >
              <Send className="h-3 w-3" />
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}

function Message({ m, onOpenNote }: { m: DraftMsg; onOpenNote: (id: string) => void }) {
  const isUser = m.role === "user";
  return (
    <article
      className={`rounded border p-2 text-sm ${
        isUser
          ? "border-zinc-800 bg-zinc-900/50 text-zinc-200"
          : "border-emerald-900/40 bg-emerald-950/10 text-zinc-100"
      }`}
    >
      <div className="mb-1 text-[10px] uppercase tracking-wider text-zinc-500">
        {isUser ? "Du" : "Claude"}
        {m.streaming && <span className="ml-2 animate-pulse text-emerald-400">…</span>}
      </div>
      <CitationRenderer text={m.content} citations={m.citations ?? []} onOpenNote={onOpenNote} />
      {!isUser && m.citations && m.citations.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1 border-t border-zinc-800 pt-2">
          {m.citations.map((c) => (
            <button
              key={c.key}
              onClick={() => onOpenNote(c.note_id)}
              className="rounded-full border border-emerald-800/60 bg-emerald-900/20 px-2 py-0.5 text-[10px] text-emerald-300 hover:border-emerald-500"
              title={`Score ${c.score.toFixed(3)}`}
            >
              [{c.key}] {c.note_title}
            </button>
          ))}
        </div>
      )}
    </article>
  );
}

function CitationRenderer({
  text,
  citations,
  onOpenNote,
}: {
  text: string;
  citations: Citation[];
  onOpenNote: (id: string) => void;
}) {
  // Render [n1], [n2] inline als Klick-Links
  const map = new Map(citations.map((c) => [c.key, c]));
  const parts = text.split(/(\[n\d+\])/g);
  return (
    <div className="whitespace-pre-wrap break-words leading-relaxed">
      {parts.map((p, i) => {
        const m = p.match(/^\[(n\d+)\]$/);
        if (m) {
          const c = map.get(m[1]);
          if (c) {
            return (
              <button
                key={i}
                onClick={() => onOpenNote(c.note_id)}
                className="mx-0.5 inline-flex items-center rounded bg-emerald-900/40 px-1 text-[10px] text-emerald-300 hover:bg-emerald-800"
                title={c.note_title}
              >
                {m[1]}
              </button>
            );
          }
        }
        return <span key={i}>{p}</span>;
      })}
    </div>
  );
}
